import { buildCommunicationTable, readCommunicationTable, updateCommunicationTable } from './communication-table';

function initializeEditors() {
    document.querySelectorAll('[data-campaign-editor]').forEach(root => {
        if (root.dataset.initialized) return;
        root.dataset.initialized = 'true';
        const form = root.closest('form');
        const rich = root.querySelector('#rich-editor');
        const code = root.querySelector('#html-editor');
        const hidden = root.querySelector('#body_html');
        const status = root.querySelector('[data-editor-status]');
        const frame = root.querySelector('[data-email-preview]');
        const imageDialog = root.querySelector('[data-image-modal] dialog');
        const imageToggle = root.querySelector('[data-upload-button]');
        const imageStatus = root.querySelector('[data-image-status]');
        const tableStatus = root.querySelector('[data-table-status]');
        const removeImage = root.querySelector('[data-remove-image]');
        let selectedImage = null;
        let codeMode = false, selection = null, busy = false, imageInserted = false;
        const message = text => { status.textContent = text; imageStatus.textContent = text; tableStatus.textContent = text; };
        const sync = () => { hidden.value = codeMode ? code.value : rich.innerHTML; };
        const remember = () => {
            const current = window.getSelection();
            if (current.rangeCount && rich.contains(current.anchorNode) && rich.contains(current.focusNode)) selection = current.getRangeAt(0).cloneRange();
        };
        const restore = () => {
            rich.focus();
            const current = window.getSelection();
            current.removeAllRanges();
            if (selection && rich.contains(selection.commonAncestorContainer)) current.addRange(selection);
            else { const range = document.createRange(); range.selectNodeContents(rich); range.collapse(false); current.addRange(range); }
        };
        const command = (name, value = null) => {
            if (codeMode) return message('Volvé al editor visual para aplicar formato o insertar una imagen.');
            restore();
            document.execCommand('styleWithCSS', false, true);
            document.execCommand(name, false, value);
            remember(); sync();
        };
        const request = async (url, data) => {
            const response = await fetch(url, { method: 'POST', credentials: 'same-origin', headers: { Accept: 'application/json', 'X-CSRF-TOKEN': form.querySelector('[name="_token"]').value }, body: data });
            const json = await response.json().catch(() => ({}));
            if (!response.ok) throw new Error(Object.values(json.errors || {}).flat().join(' ') || json.message || 'No se pudo completar la acción. Actualizá la página e intentá nuevamente.');
            return json;
        };
        const run = async action => {
            if (busy) return;
            busy = true; root.setAttribute('aria-busy', 'true');
            const disabled = [...form.querySelectorAll('button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled)')];
            disabled.forEach(el => { el.disabled = true; });
            rich.contentEditable = 'false';
            try { await action(); } catch (error) { message(error.message); }
            finally { busy = false; root.removeAttribute('aria-busy'); disabled.forEach(el => { el.disabled = false; }); rich.contentEditable = 'true'; }
        };
        const previewData = html => {
            const data = new FormData(); data.append('body_html', html);
            root.querySelectorAll('[name^="design_settings["]').forEach(el => data.append(el.name, el.value));
            return data;
        };
        root.addEventListener('pointerdown', remember);
        rich.addEventListener('keyup', remember); rich.addEventListener('mouseup', remember);
        rich.addEventListener('input', sync); code.addEventListener('input', sync);
        rich.addEventListener('click', event => {
            selectedImage = event.target.closest('img');
            removeImage.disabled = !selectedImage;
            if (selectedImage) {
                selection = document.createRange(); selection.selectNode(selectedImage);
                const current = window.getSelection(); current.removeAllRanges(); current.addRange(selection);
            }
        });
        removeImage.addEventListener('click', () => {
            if (codeMode || !selectedImage || !rich.contains(selectedImage)) return;
            selection = document.createRange(); selection.selectNode(selectedImage);
            command('delete');
            selectedImage = null; removeImage.disabled = true;
            message('Imagen quitada del mensaje. Podés deshacer con Ctrl/Cmd + Z.');
        });
        form.addEventListener('submit', event => { if (busy) event.preventDefault(); sync(); });
        root.querySelectorAll('[data-editor-command]').forEach(btn => btn.addEventListener('click', () => command(btn.dataset.editorCommand)));
        root.querySelector('[data-editor-format]').addEventListener('change', event => command('formatBlock', event.target.value));
        root.querySelectorAll('[data-editor-color]').forEach(input => input.addEventListener('change', () => command(input.dataset.editorColor, input.value)));
        root.querySelector('[data-insert-link]').addEventListener('click', () => {
            const url = prompt('URL completa del enlace (https://...)');
            if (!url) return;
            if (!/^(https?:\/\/|mailto:|tel:)/i.test(url.trim())) return message('Usá un enlace HTTP/HTTPS, mailto o tel.');
            command('createLink', url.trim());
        });
        const tableDialog = root.querySelector('[data-table-modal] dialog');
        const tableToggle = root.querySelector('[data-table-button]');
        const tableApply = root.querySelector('[data-insert-table]');
        const fields = Object.fromEntries(Object.entries({rows:'rows',columns:'columns',header:'header',borders:'borders',headerColor:'header-color',borderColor:'border-color',backgroundColor:'background-color',textColor:'text-color',headerTextColor:'header-text-color',borderWidth:'border-width',borderStyle:'border-style'})
            .map(([key, suffix]) => [key, root.querySelector(`[data-table-${suffix}]`)]));
        const tableValues = () => Object.fromEntries(Object.entries(fields).map(([key, field]) => [key, field.type === 'checkbox' ? field.checked : field.value]));
        const tableDefaults = tableValues();
        const removal = root.querySelector('[data-table-confirm-removal]');
        const removalWarning = root.querySelector('[data-table-removal-warning]');
        const deletePanel = root.querySelector('[data-table-delete-panel]');
        const deleteTable = root.querySelector('[data-delete-table]');
        const confirmDelete = root.querySelector('[data-table-confirm-delete]');
        let editingTable = null, tableApplied = false;
        const updateTableControls = () => {
            removalWarning.hidden = !editingTable || !(Number(fields.rows.value) < editingTable.rows.length || Number(fields.columns.value) < editingTable.rows[0].cells.length);
            for (const key of ['borderWidth', 'borderStyle', 'borderColor']) fields[key].disabled = !fields.borders.checked;
            fields.headerColor.disabled = fields.headerTextColor.disabled = !fields.header.checked;
        };
        Object.values(fields).forEach(field => field.addEventListener('input', () => { removal.checked = false; updateTableControls(); }));
        tableToggle.addEventListener('click', () => {
            if (codeMode) return message('Volvé al editor visual para insertar o modificar una tabla.');
            remember();
            const container = selection?.startContainer;
            const element = container?.nodeType === Node.ELEMENT_NODE ? container : container?.parentElement;
            editingTable = element && rich.contains(element) ? element.closest('table') : null;
            try {
                const values = editingTable ? readCommunicationTable(editingTable) : tableDefaults;
                for (const [key, field] of Object.entries(fields)) {
                    if (field.type === 'checkbox') field.checked = values[key];
                    else field.value = values[key];
                }
            } catch (error) { return message(error.message); }
            tableApplied = false; removal.checked = false; tableStatus.textContent = '';
            deletePanel.hidden = !editingTable; deletePanel.open = false;
            confirmDelete.checked = false; deleteTable.disabled = true;
            root.querySelector('[data-table-title]').textContent = editingTable ? 'Editar tabla' : 'Insertar tabla';
            tableApply.textContent = editingTable ? 'Aplicar cambios' : 'Insertar tabla';
            updateTableControls();
            tableDialog.showModal();
            tableToggle.setAttribute('aria-expanded', 'true');
        });
        root.querySelectorAll('[data-close-table]').forEach(button => button.addEventListener('click', () => tableDialog.close()));
        tableDialog.addEventListener('close', () => {
            tableToggle.setAttribute('aria-expanded', 'false');
            if (!tableApplied) tableToggle.focus();
        });
        tableDialog.addEventListener('keydown', event => {
            if (event.key === 'Enter' && event.target.matches('input[type="number"]')) {
                event.preventDefault(); tableApply.click();
            }
        });
        tableApply.addEventListener('click', () => {
            if (codeMode) return message('Volvé al editor visual para insertar o modificar una tabla.');
            try {
                if (editingTable && !rich.contains(editingTable)) throw new Error('La tabla ya no está en el editor. Volvé a seleccionarla.');
                const html = editingTable ? updateCommunicationTable(editingTable, tableValues(), removal.checked) : buildCommunicationTable(tableValues());
                if (editingTable) {
                    selection = document.createRange(); selection.selectNode(editingTable);
                }
                tableApplied = true;
                tableDialog.close();
                command('insertHTML', html);
                message(editingTable ? 'Tabla actualizada. Revisá la vista previa y guardá el borrador.' : 'Tabla insertada. Hacé clic en cada celda para escribir; para modificar su diseño, pulsá Tabla desde una celda.');
            } catch (error) { message(error.message); }
        });
        confirmDelete.addEventListener('change', () => { deleteTable.disabled = !confirmDelete.checked; });
        deleteTable.addEventListener('click', () => {
            if (codeMode || !confirmDelete.checked || !editingTable || !rich.contains(editingTable)) return;
            selection = document.createRange(); selection.selectNode(editingTable);
            tableApplied = true; tableDialog.close();
            command('insertHTML', '<p><br></p>');
            selectedImage = null; removeImage.disabled = true;
            message('Tabla eliminada del mensaje. Podés deshacer con Ctrl/Cmd + Z.');
        });
        root.querySelector('[data-toggle-html]').addEventListener('click', () => run(async () => {
            sync();
            if (codeMode) {
                message('Limpiando HTML…');
                const data = await request(root.dataset.previewUrl, previewData(code.value));
                rich.innerHTML = data.html; selection = null;
                message(data.warning || 'HTML seguro cargado. Revisá el diseño y las imágenes antes de guardar.');
            } else { code.value = rich.innerHTML; message('Pegá el HTML. Al volver al editor visual se aplicará la limpieza de seguridad.'); }
            codeMode = !codeMode;
            rich.classList.toggle('hidden', codeMode); code.classList.toggle('hidden', !codeMode);
            root.querySelector('[data-toggle-html]').textContent = codeMode ? 'Editor visual' : '</> HTML'; sync();
        }));
        rich.addEventListener('paste', event => {
            event.preventDefault(); remember();
            const html = event.clipboardData.getData('text/html');
            if (!html) return command('insertText', event.clipboardData.getData('text/plain'));
            run(async () => {
                message('Limpiando contenido pegado…');
                const data = await request(root.dataset.previewUrl, previewData(html));
                // Restore editability only after sanitization; never insert untrusted clipboard HTML.
                rich.contentEditable = 'true'; command('insertHTML', data.html); message(data.warning || 'Contenido pegado. Revisá las imágenes y la vista previa.');
            });
        });
        rich.addEventListener('drop', event => { event.preventDefault(); message('Usá el botón Imagen para subir archivos, o pegá el contenido con Ctrl/Cmd + V.'); });
        imageToggle.addEventListener('click', () => {
            if (codeMode) return message('Volvé al editor visual para insertar la imagen en el cursor.');
            remember();
            imageInserted = false;
            imageStatus.textContent = '';
            imageDialog.showModal();
            imageToggle.setAttribute('aria-expanded', 'true');
        });
        root.querySelectorAll('[data-close-image]').forEach(button => button.addEventListener('click', () => {
            if (!busy) imageDialog.close();
        }));
        imageDialog.addEventListener('cancel', event => {
            if (busy) event.preventDefault();
        });
        imageDialog.addEventListener('close', () => {
            imageToggle.setAttribute('aria-expanded', 'false');
            if (!imageInserted) imageToggle.focus();
        });
        imageDialog.addEventListener('keydown', event => {
            // This dialog belongs to the campaign form; Enter must not save it.
            if (event.key === 'Enter' && event.target.matches('[data-image-alt]')) {
                event.preventDefault();
                if (!busy) root.querySelector('[data-insert-image]').click();
            }
        });
        root.querySelector('[data-insert-image]').addEventListener('click', () => {
            if (codeMode) return message('Volvé al editor visual para insertar imágenes.');
            const input = root.querySelector('[data-image-file]'); const file = input.files[0];
            if (!file) return message('Seleccioná una imagen JPG o PNG.');
            if (!['image/jpeg', 'image/png'].includes(file.type) || file.size > 5 * 1024 * 1024) return message('Usá una imagen JPG o PNG de hasta 5 MB.');
            const alt = root.querySelector('[data-image-alt]').value.trim();
            run(async () => {
                message('Subiendo y optimizando imagen…'); const body = new FormData(); body.append('image', file);
                const data = await request(root.dataset.uploadUrl, body);
                const image = document.createElement('img'); image.src = data.url; image.alt = alt; image.width = Math.min(data.width, 544); image.style.cssText = 'max-width:100%;height:auto;';
                // Close before restoring the range: the background editor is inert
                // while a native modal is open.
                imageInserted = true;
                imageDialog.close();
                rich.contentEditable = 'true'; command('insertHTML', `<p>${image.outerHTML}</p><p><br></p>`);
                input.value = ''; root.querySelector('[data-image-alt]').value = '';
                message('Imagen insertada. Guardá el borrador para conservarla y habilitar su visualización pública.');
            });
        });
        root.querySelectorAll('[data-preview]').forEach(button => button.addEventListener('click', () => run(async () => {
            sync(); message('Generando vista previa…');
            const data = await request(root.dataset.previewUrl, previewData(hidden.value));
            frame.style.width = button.dataset.preview === 'mobile' ? '375px' : '100%'; frame.srcdoc = data.document; frame.hidden = false;
            message(data.warning || 'Vista previa actualizada. La apariencia final puede variar entre Gmail, Outlook y otros clientes de correo.');
        })));
    });
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initializeEditors);
else initializeEditors();
document.addEventListener('livewire:navigated', initializeEditors);
